import {
	Accordion,
	AccordionDetails,
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Snackbar,
	TextField,
} from '@mui/material';
import AccordionSummary from './StyledSectionAccordionSummary';
import React from 'react';
import type { SavedSearch } from '../../../api/models/savedSearch.model';
import {
	useGetCurrentUserQuery,
	useDeleteSavedSearchMutation,
	useGetSavedSearchesQuery,
	useUpdateSavedSearchMutation,
} from '../../../api/user.api.ts';
import { Alert, CircularProgress, Typography } from '@mui/material';
import isEqual from 'react-fast-compare';

const shouldBeExpanded = (findAFilterValue: string, filterExpanded: boolean) => {
	if (filterExpanded) {
		return true;
	}
	return findAFilterValue.length > 0;
};

interface SavedSearchesAccordionProps {
	findAFilterValue: string;
	pendingFilterValues?: { [key: string]: any };
	onSelectSavedSearch?: (filters: Record<string, any>) => void;
}

function SavedSearchesAccordion({
	findAFilterValue = '',
	pendingFilterValues = {},
	onSelectSavedSearch,
}: SavedSearchesAccordionProps) {
	const [filterExpanded, setFilterExpanded] = React.useState<boolean>(false);
	const [selectedSearch, setSelectedSearch] = React.useState<SavedSearch | null>(null);
	const [originalFilters, setOriginalFilters] = React.useState<Record<string, any> | null>(null);
	const [deleteSearchModalOpen, setDeleteSearchModalOpen] = React.useState<boolean>(false);

	const { data: user } = useGetCurrentUserQuery();

	const {
		data: savedSearches,
		isLoading: isLoadingSavedSearches,
		isError: isErrorLoadingSavedSearches,
	} = useGetSavedSearchesQuery(undefined, { skip: !user || !user.userId });

	const [
		updateSavedSearch,
		{ isSuccess: isSavedSearchUpdated, isError: isSavedSearchUpdateError },
	] = useUpdateSavedSearchMutation();

	const [
		deleteSavedSearch,
		{ isSuccess: isSavedSearchDeleted, isError: isSavedSearchDeleteError },
	] = useDeleteSavedSearchMutation();

	const [showSuccessAlert, setShowSuccessAlert] = React.useState(false);
	const [showErrorAlert, setShowErrorAlert] = React.useState(false);

	React.useEffect(() => {
		if (isSavedSearchUpdateError || isSavedSearchDeleteError) {
			setShowErrorAlert(true);
		}
	}, [isSavedSearchUpdateError, isSavedSearchDeleteError]);

	React.useEffect(() => {
		if (isSavedSearchUpdated || isSavedSearchDeleted) {
			setShowSuccessAlert(true);
		}
	}, [isSavedSearchUpdated, isSavedSearchDeleted]);

	const handleCloseAlert = (_event?: React.SyntheticEvent | Event, reason?: string) => {
		if (reason === 'clickaway') return;
		setShowSuccessAlert(false);
		setShowErrorAlert(false);
	};

	const handleChange = (_event: any, value: SavedSearch | null) => {
		setSelectedSearch(value);
		setOriginalFilters(value?.filters || {});
		if (onSelectSavedSearch) {
			onSelectSavedSearch(value?.filters || {});
		}
	};

	// Assume currentFilters come from parent via props or context
	const currentFilters = pendingFilterValues ?? {};

	const isDirty = React.useMemo(() => {
		if (!selectedSearch) return false;
		return !isEqual(currentFilters, originalFilters);
	}, [currentFilters, originalFilters, selectedSearch]);

	const saveChanges = (): void => {
		const filtersToSave = pendingFilterValues;
		updateSavedSearch({
			searchId: Number(selectedSearch?.id),
			name: selectedSearch?.name || '',
			filters: filtersToSave,
		});
	};

	const deleteSearch = () => {
		setDeleteSearchModalOpen(false);
		setSelectedSearch(null);
		deleteSavedSearch({
			searchId: Number(selectedSearch?.id),
		});
	};

	const getAlertSeverity = () => {
		if (isSavedSearchUpdated || isSavedSearchDeleted) return 'success';
		if (isSavedSearchUpdateError || isSavedSearchDeleteError) return 'error';
		return 'info';
	};

	const getAlertMessage = () => {
		if (isSavedSearchUpdated) return 'Search saved successfully.';
		if (isSavedSearchUpdateError) return 'Failed to save search.';
		if (isSavedSearchDeleted) return 'Search deleted successfully.';
		if (isSavedSearchDeleteError) return 'Failed to delete search.';
		return '';
	};

	return (
		<>
			<Accordion
				key="savedSearches"
				expanded={shouldBeExpanded(findAFilterValue, filterExpanded)}
				onChange={() => setFilterExpanded((prev) => !prev)}
				data-testid={`filter-accordion-section-savedSearches`}
				sx={{
					'&::before': {
						display: 'none', // removes the horizontal border
					},
				}}
			>
				<AccordionSummary>{'Saved Searches'}</AccordionSummary>
				<AccordionDetails>
					{isErrorLoadingSavedSearches ? (
						<Alert severity="error" data-testid="saved-searches-error">
							Error loading saved searches
						</Alert>
					) : isLoadingSavedSearches ? (
						<Box
							display="flex"
							alignItems="left"
							gap={2}
							data-testid="saved-searches-loading"
							sx={{ mt: 1 }}
						>
							<CircularProgress size={24} />
							<Typography variant="body2">Loading saved searches...</Typography>
						</Box>
					) : (
						<>
							<Box sx={{ mt: 1 }}>
								<Autocomplete
									data-testid={'saved-searches-dropdown'}
									options={savedSearches || []}
									value={selectedSearch}
									getOptionLabel={(option) => option.name}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									onChange={handleChange}
									freeSolo={false}
									renderInput={(params) => (
										<TextField {...params} label="Search" placeholder="Search" />
									)}
								/>
								<Box display="flex" gap={1} marginTop={2} justifyContent="left">
									<Box display="flex" justifyItems="left" sx={{ cursor: 'pointer' }}>
										<Button
											variant="text"
											sx={{color: (theme) => isDirty ? theme.palette.brand.primary : 'grey'}}
											style={{
												fontSize: 13,
												opacity: isDirty ? 1 : 0.5,
											}}
											disabled={!isDirty}
											onClick={saveChanges}
										>
											SAVE CHANGES TO SEARCH
										</Button>
									</Box>
									<Box display="flex" justifyItems="left" sx={{ cursor: 'pointer' }}>
										<Button
											variant="text"
											sx={{color: (theme) => theme.palette.brand.primary}}
											style={{
												fontSize: 13,
												opacity: selectedSearch ? 1 : 0.5,
											}}
											disabled={!selectedSearch}
											onClick={() => setDeleteSearchModalOpen(true)}
										>
											DELETE SEARCH
										</Button>
									</Box>
								</Box>
							</Box>
						</>
					)}
				</AccordionDetails>
			</Accordion>
			<Dialog
				open={deleteSearchModalOpen}
				onClose={() => setDeleteSearchModalOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Delete</DialogTitle>
				<DialogContent>
					Are you sure you want to delete this saved search? This action can't be undone.
				</DialogContent>
				<DialogActions sx={{ mb: 2 }}>
					<Button
						variant="outlined"
						onClick={() => setDeleteSearchModalOpen(false)}
						sx={{
							fontSize: 13,
							borderRadius: '20px',
						}}
					>
						Cancel
					</Button>
					<Button
						data-testid="delete-saved-search-confirmation"
						variant="contained"
						color="primary"
						sx={{
							fontSize: 13,
							borderRadius: '20px',
						}}
						onClick={deleteSearch}
					>
						Delete Saved Search
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={showSuccessAlert || showErrorAlert}
				autoHideDuration={6000}
				onClose={handleCloseAlert}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleCloseAlert}
					severity={getAlertSeverity()}
					variant="filled"
					sx={{ width: '100%' }}
				>
					{getAlertMessage()}
				</Alert>
			</Snackbar>
		</>
	);
}

export default SavedSearchesAccordion;
